import { queryOptions, useQuery } from "@tanstack/react-query";
import { getAuthorizationHeader } from "../../Auth/index.js";
import { resolveBrandclubHostDomain } from "../../utils/domain.js";
var CachedQueryKeys = /* @__PURE__ */ ((CachedQueryKeys2) => {
  CachedQueryKeys2["AppConfig"] = "appConfig";
  CachedQueryKeys2["AppTheme"] = "appTheme";
  CachedQueryKeys2["UserCart"] = "userCart";
  CachedQueryKeys2["BrandAppConfigs"] = "brandAppConfigs";
  CachedQueryKeys2["BrandClub"] = "brandClub";
  CachedQueryKeys2["DTCProduct"] = "dtcProduct";
  CachedQueryKeys2["SiteProduct"] = "siteProduct";
  CachedQueryKeys2["UserProfile"] = "userProfile";
  CachedQueryKeys2["ThemeSetup"] = "themeSetup";
  CachedQueryKeys2["SuggestedQueries"] = "suggestedQueries";
  return CachedQueryKeys2;
})(CachedQueryKeys || {});
const THIRTY_MINUTES_IN_MS = 1e3 * 60 * 30;
const FIVE_MINUTES_IN_MS = 1e3 * 60 * 5;
const getCommonRequestHeaders = () => {
  return {
    // "X-Client-AppId": ConfigUtils.getWebAppId(), // TODO: Temporarily do not send this header
  };
};
const BRANDAPP_V2_ALLOWED_MODES = /* @__PURE__ */ new Set([
  "off",
  "shim",
  "consume"
]);
const isBrandAppV2Mode = (value) => typeof value === "string" && BRANDAPP_V2_ALLOWED_MODES.has(value);
const getBrandAppV2Mode = () => {
  var _a;
  try {
    if (typeof window === "undefined") return "off";
    const value = (_a = window.localStorage) == null ? void 0 : _a.getItem("brandapp-v2");
    if (!value) return "off";
    return isBrandAppV2Mode(value) ? value : "off";
  } catch {
    return "off";
  }
};
let _bootstrapPromise = null;
const getBootstrapShared = async (signal, apiBaseUrl) => {
  if (!_bootstrapPromise) {
    const DOMAIN = apiBaseUrl || resolveBrandclubHostDomain();
    const inflight = (async () => {
      const res = await fetch(DOMAIN + "/api-rewards/brandapp-v2/bootstrap", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getCommonRequestHeaders(),
          ...await getAuthorizationHeader()
        },
        signal
      });
      if (!res.ok) {
        throw new Error(`Error fetching V2 bootstrap (status ${res.status})`);
      }
      return res.json();
    })();
    _bootstrapPromise = inflight;
    inflight.then(
      () => {
        if (_bootstrapPromise === inflight) _bootstrapPromise = null;
      },
      () => {
        if (_bootstrapPromise === inflight) _bootstrapPromise = null;
      }
    );
  }
  return _bootstrapPromise;
};
const bootstrapToAppConfig = (b) => {
  const cognito = b.auth.cognito;
  if (!cognito) {
    throw new Error("brandapp-v2 consume: bootstrap missing auth.cognito");
  }
  return {
    authConfig: {
      userPoolId: cognito.userPoolId,
      userPoolClientId: cognito.userPoolClientId,
      identityPoolId: cognito.identityPoolId,
      signInDomain: cognito.signInDomain,
      region: cognito.region
    },
    domainConfig: {
      domainName: b.identity.hostname,
      brandId: b.identity.brand.brandId,
      brandName: b.identity.brand.brandName,
      storeBrandingType: b.identity.brand.storeBrandingType ?? "CustomStore",
      customShopDomainName: b.identity.brand.customShopDomainName,
      mobileAppLink: b.identity.brand.mobileAppLink,
      pixels: b.tracking.pixels,
      abTestingTreatmentResults: b.tracking.abTesting,
      customShopPageUrls: b.identity.brand.customShopPageUrls,
      lduAffectedStates: b.tracking.lduAffectedStates
    }
  };
};
const bootstrapToBrandAppConfig = (b) => {
  const distribution = b.capabilities.distribution;
  const deepLink = b.capabilities.deepLinking;
  return {
    appConfigId: b.app.appId,
    version: "CURRENT",
    appName: b.app.appName,
    companyId: "",
    appStatus: "active",
    brandIds: b.app.brandIds,
    brandclubIds: b.app.brandclubIds.map(String),
    retailerIds: b.app.retailerIds,
    updatedBy: "",
    updatedTime: "",
    publishedVersion: 0,
    appId: b.app.appId,
    appType: b.app.appType,
    appStoreUrl: (distribution == null ? void 0 : distribution.appStoreUrl) ?? "",
    playStoreUrl: (distribution == null ? void 0 : distribution.playStoreUrl) ?? "",
    configId: b.app.appId,
    apps: [
      {
        appConfigId: b.app.appId,
        appDescription: b.app.description ?? "",
        appId: b.app.appId,
        appName: b.app.appName,
        appSlug: (distribution == null ? void 0 : distribution.appSlug) ?? "",
        appStatus: "active",
        appType: b.app.appType
      }
    ],
    branchIOConfig: {
      webAppBranchLink: (deepLink == null ? void 0 : deepLink.webAppBranchLink) ?? "",
      mobileAppBranchLink: (deepLink == null ? void 0 : deepLink.mobileAppBranchLink) ?? "",
      accessToken: "",
      key: (deepLink == null ? void 0 : deepLink.apiKey) ?? "",
      appId: (deepLink == null ? void 0 : deepLink.branchAppId) ?? ""
    }
  };
};
const getAppConfig = async (signal) => {
  const mode = getBrandAppV2Mode();
  if (mode === "consume") {
    const bootstrap = await getBootstrapShared(signal);
    return bootstrapToAppConfig(bootstrap);
  }
  const DOMAIN = resolveBrandclubHostDomain();
  const path = mode === "shim" ? "/api-rewards/brandapp-v2/webAppConfig" : "/api-rewards/config/webApp/getAppConfig";
  const res = await fetch(DOMAIN + path, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getCommonRequestHeaders()
      // Note: getAuthorizationHeader would need to be imported from common-ui
      // For now, we'll assume it's available or handle auth differently
    },
    signal
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching app config");
  }
};
const getBrandAppConfigs = async (signal, apiBaseUrl) => {
  const mode = getBrandAppV2Mode();
  if (mode === "consume") {
    const bootstrap = await getBootstrapShared(signal, apiBaseUrl);
    return bootstrapToBrandAppConfig(bootstrap);
  }
  const DOMAIN = apiBaseUrl || resolveBrandclubHostDomain();
  const path = mode === "shim" ? "/api-rewards/brandapp-v2/brandAppConfig" : "/api-rewards/brandapp/brandAppConfig";
  const res = await fetch(DOMAIN + path, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...getCommonRequestHeaders(),
      ...await getAuthorizationHeader()
    },
    signal
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error("Error fetching brand app configs");
  }
};
const getSuggestedQueries = async (_signal, brandId, apiBaseUrl) => {
  const DOMAIN = apiBaseUrl || resolveBrandclubHostDomain();
  const url = new URL(DOMAIN + "/api-rewards/concierge/suggestedQueries");
  if (brandId) {
    url.searchParams.set("brandId", brandId.toString());
  }
  try {
    const authHeaders = await getAuthorizationHeader();
    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getCommonRequestHeaders(),
        ...authHeaders
      }
    });
    if (res.ok) {
      const data = await res.json();
      return data;
    } else {
      console.error("getSuggestedQueries - error response:", res.status, res.statusText);
      throw new Error("Error fetching suggested queries");
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { suggestedQuestions: [] };
    }
    console.error("getSuggestedQueries - caught error:", error);
    throw error;
  }
};
const brandAppConfigQueryOptions = (apiBaseUrl) => {
  return queryOptions({
    queryKey: [
      "brandAppConfigs"
      /* BrandAppConfigs */
    ],
    queryFn: ({ signal }) => getBrandAppConfigs(signal, apiBaseUrl),
    staleTime: THIRTY_MINUTES_IN_MS
  });
};
const appConfigQueryOptions = () => {
  return queryOptions({
    queryKey: [
      "appConfig"
      /* AppConfig */
    ],
    queryFn: ({ signal }) => getAppConfig(signal),
    // since config data rarely changes, we can use the cached data for longer and refresh it every 30 minutes
    staleTime: THIRTY_MINUTES_IN_MS
  });
};
const suggestedQueriesQueryOptions = (brandId, apiBaseUrl) => {
  return queryOptions({
    queryKey: ["suggestedQueries", brandId],
    queryFn: ({ signal }) => getSuggestedQueries(signal, brandId, apiBaseUrl),
    staleTime: FIVE_MINUTES_IN_MS
  });
};
const useAppConfig = (args) => {
  return useQuery({
    ...appConfigQueryOptions(),
    ...args,
    select: args == null ? void 0 : args.select
  });
};
const useDomainConfig = (args) => {
  const { data: appConfig, ...rest } = useAppConfig(args);
  return {
    data: appConfig == null ? void 0 : appConfig.domainConfig,
    ...rest
  };
};
const useBrandAppConfig = (apiBaseUrl, args) => {
  return useQuery({
    ...brandAppConfigQueryOptions(apiBaseUrl),
    ...args,
    select: args == null ? void 0 : args.select
  });
};
const useSuggestedQueries = (brandId, apiBaseUrl, args) => {
  return useQuery({
    ...suggestedQueriesQueryOptions(brandId, apiBaseUrl),
    ...args,
    select: args == null ? void 0 : args.select
  });
};
export {
  BRANDAPP_V2_ALLOWED_MODES,
  CachedQueryKeys,
  FIVE_MINUTES_IN_MS,
  THIRTY_MINUTES_IN_MS,
  appConfigQueryOptions,
  bootstrapToAppConfig,
  bootstrapToBrandAppConfig,
  brandAppConfigQueryOptions,
  getBootstrapShared,
  getBrandAppV2Mode,
  isBrandAppV2Mode,
  suggestedQueriesQueryOptions,
  useAppConfig,
  useBrandAppConfig,
  useDomainConfig,
  useSuggestedQueries
};
//# sourceMappingURL=appConfigQueryOptions.js.map
